import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ApiErrorEnvelope, ImpersonationExchangeDto } from '@his/shared';
import { session } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Alert, Card, CardContent, CardHeader, Spinner } from '@/components/ui/primitives';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';
const attempted = new Set<string>();

type Stage = { kind: 'working' } | { kind: 'failed'; message: string } | { kind: 'ended' };

async function exchange(code: string, tenant: string): Promise<ImpersonationExchangeDto> {
  const response = await fetch(`${BASE}/auth/impersonation/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Tenant-Id': tenant },
    body: JSON.stringify({ code }),
  });
  const payload = (await response.json().catch(() => null)) as ImpersonationExchangeDto | ApiErrorEnvelope | null;
  if (!response.ok) {
    const message = (payload as ApiErrorEnvelope | null)?.error?.message;
    throw new Error(message ?? 'This sign-in link could not be used.');
  }
  return payload as ImpersonationExchangeDto;
}

function readHandoff(): { code: string; tenant: string } | null {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const code = params.get('c');
  const tenant = params.get('t');
  return code && tenant ? { code, tenant } : null;
}

export function ImpersonatePage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [stage, setStage] = useState<Stage>(() => (window.location.hash ? { kind: 'working' } : { kind: 'ended' }));

  useEffect(() => {
    const handoff = readHandoff();
    window.history.replaceState(null, '', '/impersonate');
    if (!handoff) {
      setStage({ kind: 'ended' });
      return;
    }
    if (attempted.has(handoff.code)) return;
    attempted.add(handoff.code);
    exchange(handoff.code, handoff.tenant)
      .then(async (result) => {
        session.setImpersonation({ accessToken: result.accessToken, tenant: result.tenantSlug });
        await refreshUser();
        navigate('/dashboard', { replace: true });
      })
      .catch((error: unknown) => setStage({ kind: 'failed', message: error instanceof Error ? error.message : 'This sign-in link could not be used.' }));
  }, [navigate, refreshUser]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-lg font-semibold">Owner sign-in</h1>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {stage.kind === 'working' && (
            <p className="flex items-center gap-2 text-muted-foreground">
              <Spinner /> Opening the facility…
            </p>
          )}
          {stage.kind === 'failed' && <Alert variant="destructive">{stage.message}</Alert>}
          {stage.kind === 'ended' && (
            <p className="text-muted-foreground">
              The Owner session in this tab has ended. You can close the tab, or start a new session from the Owner console.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
